import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import Loading from 'components/Loading';
import { Card, ItemsList } from 'components/UI';
import { FormsActions } from 'state/modules/forms';
import { Box, Typography } from '@mui/material';
import Images from 'components/Forms/Images';
import DamageInspection from '../DamageInspection';
import { getResponse, getResponseArray } from 'utils';
import Rating from '../Rating';
import Generic from '../Generic';

const FormsWidget = ({ id, type }) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 5,
  });
  const { loading, list, pagination, fetchedCreateForm } = useSelector(
    (state) => state.forms || {}
  );

  /**
   * Get comments
   * @param {object} params
   */
  const getForms = (params) => {
    const payload = {
      ...params,
    };
    if (id) {
      payload.source_id = id;
    }
    if (type) {
      payload.source_type = type;
    }
    dispatch(FormsActions.fetchForms(payload));
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    getForms(newParams);
  };

  /**
   * Handle limit / page size change
   * @param {object} event Value of the input
   */
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    getForms(newParams);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];

  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="form-title">
          {i18n.t('forms.title_list')}
        </Box>
      </Typography>
    </Box>
  );

  useEffect(() => {
    getForms(params);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (fetchedCreateForm) {
      getForms(params);
    }
  }, [fetchedCreateForm]); // eslint-disable-line

  const ratingComponent = (rating, comment) => <Rating rating={rating} comment={comment} />;

  const imagesComponent = (images, event) => {
    return <Images images={images} event={event} />;
  };

  const genericComponent = (form) => {
    return <Generic form={form} />;
  };

  const damageInspectionComponent = (form) => {
    const event = getResponse(i18n.t('forms.event'), form);
    const comments = getResponse(i18n.t('forms.comments'), form);
    const damageImage = getResponse(i18n.t('forms.damage_image'), form) || {};
    const damageLocation = getResponse(i18n.t('forms.damage_location'), form);
    const images = damageImage?.reference_url ? [damageImage.reference_url] : [];

    return (
      <DamageInspection
        event={event}
        images={images}
        comments={comments}
        damageImage={damageImage}
        damageLocation={damageLocation}
      />
    );
  };

  const processResponses = (form) => {
    let rating = 0;
    let event;
    let images;
    let comment;

    switch (form?.form_template?.name) {
      case i18n.t('forms.cleanliness'):
        rating = getResponse(i18n.t('forms.clean_rating'), form);
        comment = getResponse(i18n.t('forms.clean_comment'), form);
        return ratingComponent(rating.response, comment?.response);

      case i18n.t('forms.experience'):
        rating = getResponse(i18n.t('forms.experience_rating'), form);
        comment = getResponse(i18n.t('forms.experience_comment'), form);
        return ratingComponent(rating.response, comment?.response);

      case i18n.t('forms.vehicle_photo'):
        event = getResponse(i18n.t('forms.event'), form);
        images = getResponseArray(i18n.t('forms.vehicle_image'), form);
        return imagesComponent(images, event);

      case i18n.t('forms.driver_damage_inspection'):
        return damageInspectionComponent(form);

      case i18n.t('forms.fuel_capture'): {
        event = getResponse(i18n.t('forms.event'), form);
        images = getResponseArray(i18n.t('forms.fuel_capture'), form);
        images = images.sort((val1, val2) => (val1.id > val2.id ? -1 : 1));
        const defaultImages = images?.[0] ? [images[0]] : [];
        return imagesComponent(defaultImages, event);
      }
      default:
        return genericComponent(form);
    }
  };

  const processList = (list = []) => {
    return list?.map((item) => {
      const { id, display_name, created_time, creation_user } = item;
      return {
        id,
        display_name,
        opened_time: created_time,
        owner_user: creation_user,
        type: 'normal',
        collapsible: true,
        collapsibleContent: processResponses(item),
      };
    });
  };

  return (
    <Card title={title} menuItems={items} data-cy="form-card" headerActionType="vertical">
      <Box sx={{ height: '100%', width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              height: 150,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Loading />
          </Box>
        ) : (
          <ItemsList
            list={processList(list)}
            params={params}
            scrollable={false}
            pagination={pagination}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            placeholder={i18n.t('forms.forms_placeholder')}
            userLabel={i18n.t('forms.create_by')}
            dateLabel={i18n.t('forms.create_date')}
            idLabel={`Form #`}
            handleItemClick={() => {}}
          />
        )}
      </Box>
    </Card>
  );
};

FormsWidget.propTypes = {
  id: PropTypes.number,
  type: PropTypes.string,
};

export default FormsWidget;
